import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { DashboardMock } from "./dashboard";
import { useSceneAutoplay, SceneType } from "@/hooks/use-scene-autoplay";
import { Play, BarChart3, Users, Zap, Filter, Shield, Video, TrendingUp, Target, LineChart } from "lucide-react";

// Floating decorative icons component
function FloatingIcons() {
  useLanguage();
  const icons = [
    // Top row
    { Icon: Play, className: "top-[8%] left-[6%] animate-float-slow", size: 30 },
    { Icon: Video, className: "top-[12%] left-[22%] animate-float-slow-reverse", size: 22 },
    { Icon: Users, className: "top-[10%] right-[8%] animate-float-slow-reverse", size: 26 },
    // Middle row
    { Icon: Filter, className: "top-[38%] left-[4%] animate-float-slow-reverse", size: 24 },
    { Icon: TrendingUp, className: "top-[45%] right-[5%] animate-float-slow", size: 22 },
    { Icon: Shield, className: "top-[32%] right-[18%] animate-float-slow", size: 20 },
    // Bottom row
    { Icon: Target, className: "bottom-[18%] left-[8%] animate-float-slow", size: 24 },
    { Icon: Zap, className: "bottom-[25%] left-[20%] animate-float-slow-reverse", size: 20 },
    { Icon: LineChart, className: "bottom-[22%] right-[18%] animate-float-slow", size: 22 },
    { Icon: BarChart3, className: "bottom-[10%] right-[6%] animate-float-slow-reverse", size: 28 },
  ];

  return (
    <>
      {icons.map(({ Icon, className, size }, index) => (
        <Icon
          key={index}
          size={size}
          className={`absolute text-white/25 pointer-events-none ${className}`}
          aria-hidden="true"
          style={{ animationDelay: `${index * 0.5}s` }}
        />
      ))}
    </>
  );
}

interface SceneStepperProps {
  scenes: SceneType[];
  currentScene: SceneType;
  onSceneClick: (scene: SceneType) => void;
}

const sceneLabels: Record<SceneType, string> = {
  upload: "Upload",
  leads: "Leads",
  funnel: "Funil de Vídeo",
  metrics: "Métricas",
  abtest: "Teste A/B",
};

function SceneStepper({ scenes, currentScene, onSceneClick }: SceneStepperProps) {
  useLanguage();
  return (
    <div className="flex items-center justify-center gap-2 mt-6">
      {scenes.map((scene) => {
        const isActive = scene === currentScene;
        return (
          <button
            key={scene}
            onClick={() => onSceneClick(scene)}
            className={`
              px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all duration-300
              ${isActive
                ? "bg-white/20 text-white shadow-lg"
                : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80"
              }
            `}
            aria-pressed={isActive}
          >
            {sceneLabels[scene]}
          </button>
        );
      })}
    </div>
  );
}

export function VisualPanel() {
  useLanguage();
  const {
    currentScene,
    scenes,
    goToScene,
    handleMouseEnter,
    handleMouseLeave,
  } = useSceneAutoplay();

  return (
    <div
      className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden px-4 py-8 lg:px-8 lg:py-12"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Floating decorative icons */}
      <FloatingIcons />

      {/* Content Rail - alinha texto + modal + chips no mesmo eixo */}
      <div className="flex flex-col items-center w-full max-w-[480px] lg:max-w-[500px] mx-auto relative z-10">
        {/* Text Block */}
        <div className="text-center mb-6 lg:mb-8 w-full">
          <h1
            className="visual-panel-headline text-[28px] md:text-[38px] xl:text-[44px] font-extrabold text-white mb-2 animate-headline-enter whitespace-nowrap relative lg:left-[-8px]"
            style={{
              fontFamily: "'Sora', sans-serif",
              lineHeight: '1.1',
              letterSpacing: '-0.02em'
            }}
          >
            {translateInterfaceText("Mais do que um player de vídeo")}</h1>
          <p
            className="!text-[22px] !md:text-[28px] !xl:text-[32px] font-bold text-white mb-3 animate-headline-enter whitespace-nowrap"
            style={{
              fontFamily: "'Sora', sans-serif",
              lineHeight: '1.2',
              letterSpacing: '-0.01em'
            }}
          >
            {translateInterfaceText("Uma máquina de conversão")}</p>
          <p
            className="visual-panel-subheadline !text-[14px] !md:text-[15px] font-medium text-white/85 animate-subheadline-enter"
            style={{ lineHeight: '1.55' }}
          >
            {translateInterfaceText("Tudo para escalar VSLs com performance, proteção e métricas.")}</p>
        </div>

        {/* Dashboard Mock */}
        <div aria-label={translateInterfaceText("Demonstração do dashboard Host VSL")}>
          <DashboardMock currentScene={currentScene} />
        </div>

        {/* Scene Stepper */}
        <SceneStepper
          scenes={scenes}
          currentScene={currentScene}
          onSceneClick={goToScene}
        />
      </div>

    </div>
  );
}
